import React from 'react';
import { Box } from '@mui/material';
import KpiCard, { type KpiVariant } from './KpiCard';
import CompactMetricBox from './CompactMetricBox';

// ── Primary KPI metric descriptor ──

export interface MetricItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  value: number;
  description?: string;
  variant?: KpiVariant;
  neutralWhenZero?: boolean;
  onClick?: () => void;
  selected?: boolean;
  ariaLabel?: string;
  loading?: boolean;
}

// ── Compact context metric descriptor ──

export interface CompactMetricItem {
  key: string;
  label: string;
  value: number | string;
  ariaLabel?: string;
  tooltip?: string;
  onClick?: () => void;
  selected?: boolean;
}

// ── Props ──

interface IncidentMetricStripProps {
  /** Primary KPI cards (To Account, Accounted, Not Accounted). */
  metrics: MetricItem[];
  /** Compact context metrics (Full Roster, No Badge Today). */
  contextMetrics?: CompactMetricItem[];
}

// ── Component ──

/**
 * Renders primary KpiCards and optional compact context boxes in a single CSS
 * grid row.  Primary cards get equal 1fr columns; compact boxes get narrower
 * fractional columns and vertically centre within the row.
 */
const IncidentMetricStrip: React.FC<IncidentMetricStripProps> = ({
  metrics,
  contextMetrics = [],
}) => {
  const totalCols = metrics.length + contextMetrics.length;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm:
            totalCols === 5
              ? 'minmax(135px, 1fr) minmax(135px, 1fr) minmax(135px, 1fr) minmax(105px, 0.58fr) minmax(120px, 0.68fr)'
              : `repeat(${totalCols}, minmax(0, 1fr))`,
          lg:
            totalCols === 5
              ? 'minmax(170px, 1fr) minmax(170px, 1fr) minmax(170px, 1fr) minmax(130px, 0.6fr) minmax(150px, 0.7fr)'
              : `repeat(${totalCols}, minmax(0, 1fr))`,
        },
        gap: { xs: 1, sm: 1.25, md: 1.5 },
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        '& > *': { minWidth: 0 },
      }}
    >
      {metrics.map((m) => (
        <KpiCard
          key={m.key}
          icon={m.icon}
          label={m.label}
          value={m.value}
          description={m.description}
          variant={m.variant}
          neutralWhenZero={m.neutralWhenZero}
          onClick={m.onClick}
          selected={m.selected}
          ariaLabel={m.ariaLabel}
          loading={m.loading}
        />
      ))}
      {contextMetrics.map((cm) => (
        <CompactMetricBox
          key={cm.key}
          label={cm.label}
          value={cm.value}
          ariaLabel={cm.ariaLabel}
          tooltip={cm.tooltip}
          onClick={cm.onClick}
          selected={cm.selected}
        />
      ))}
    </Box>
  );
};

export default IncidentMetricStrip;
